import React, { useMemo, useState } from 'react';
import { Button, Card, Collapse, Input, Space, Tag, Typography, Empty } from 'antd';
import {
  BookOutlined,
  CarOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  NotificationOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  ShopOutlined,
  TeamOutlined,
  WarningOutlined,
  CustomerServiceOutlined,
  RightOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { routes } from '../../utils/routes.config';
import './userguide.css';

const { Title, Text, Paragraph } = Typography;

const FAQ = [
  {
    key: 'getting-started',
    icon: <RocketOutlined />,
    iconBg: '#eff6ff',
    iconColor: '#2563eb',
    title: 'Getting Started',
    desc: 'Account setup, role permissions, and initial workspace onboarding.',
    items: [
      {
        q: 'How do I log in to the system?',
        a: 'Use your registered administrator email and password at the /login page. Super Administrators are automatically directed to the multi-store management dashboard, while Store Owners/Staff land on their dedicated operations portal.',
      },
      {
        q: 'Why are some modules hidden in my sidebar navigation?',
        a: 'The application enforces role-based access control (RBAC). Super Admins, Store Owners, and Staff members have customized permission levels. If you require access to Orders, Drivers, or Settings, contact your administrator.',
      },
      {
        q: 'What does "store-scoped data" mean?',
        a: 'Every driver, warehouse, and shipment is strictly partitioned to your individual store ID. Merchants cannot view or interact with orders from other stores, ensuring enterprise-grade data isolation.',
      },
      {
        q: 'What is the recommended setup workflow after first login?',
        a: 'Follow this 5-step checklist: 1) Add your primary Warehouse address with map coordinates, 2) Register Drivers and configure vehicles, 3) Connect Shopify or use API Keys to ingest orders, 4) Dispatch pending orders, and 5) Monitor real-time status in Live Tracking.',
      },
    ],
  },
  {
    key: 'orders',
    icon: <ShopOutlined />,
    iconBg: '#ecfdf5',
    iconColor: '#10b981',
    title: 'Orders & Dispatch',
    desc: 'Pending order queues, assignment flows, and shipment creation.',
    items: [
      {
        q: 'What is the "Pending Order" tab?',
        a: 'The Pending Order queue lists all incoming orders awaiting warehouse selection and driver assignment. Once a driver is allocated, the order transitions into "Assigned" or "In Transit".',
      },
      {
        q: 'How do orders get ingested into the portal?',
        a: 'Orders sync automatically via Shopify Webhooks or through the secure REST API (/api/orders/create) using your HMAC-SHA256 signature and API keys.',
      },
      {
        q: 'How do I assign a driver and warehouse to an order?',
        a: 'Navigate to Orders → Pending Order. Click the Assign icon in the Driver column. Select your dispatch Warehouse (auto-selected if only one exists), choose an active driver, pick the payment mode (COD / Prepaid), and confirm.',
      },
      {
        q: 'Why is the Warehouse dropdown disabled during assignment?',
        a: 'If your store has only one configured warehouse, the system automatically selects it and locks the field to eliminate misclicks and speed up dispatch.',
      },
      {
        q: 'How do I view comprehensive order and customer history?',
        a: 'Click on any order row or the details view icon to open the full modal showing item lists, delivery coordinates, customer contact, and delivery proof.',
      },
    ],
  },
  {
    key: 'drivers',
    icon: <TeamOutlined />,
    iconBg: '#f5f3ff',
    iconColor: '#8b5cf6',
    title: 'Driver Fleet Management',
    desc: 'Onboarding drivers, verification documents, and active shifts.',
    items: [
      {
        q: 'How do I onboard a new delivery driver?',
        a: 'Go to Drivers → Add Driver. Provide personal details (Name, Phone, Email, Vehicle Type) and attach KYC documents (Driving License, National ID). Credentials for the Driver Mobile App are created automatically.',
      },
      {
        q: 'How does a driver toggle between Online and Offline duty?',
        a: 'Drivers control their shift status directly inside the Magecomp Driver Mobile App. The admin dashboard displays current shift state in real time with status tags.',
      },
      {
        q: 'Why does an active driver not show up in the assignment picker?',
        a: 'A driver is only selectable if they belong to your store, have an active non-blocked account status, and are marked available for dispatches.',
      },
    ],
  },
  {
    key: 'warehouse',
    icon: <EnvironmentOutlined />,
    iconBg: '#fffbeb',
    iconColor: '#f59e0b',
    title: 'Warehouse & Fulfillment Hubs',
    desc: 'Multi-warehouse setup, geo-coordinates, and stock locations.',
    items: [
      {
        q: 'How do I configure a new fulfillment warehouse?',
        a: 'Navigate to Warehouse → Add Warehouse. Enter the warehouse name, operational address, and use the interactive OpenStreetMap to pin precise coordinates or click "Use current location".',
      },
      {
        q: 'How does the "Delivery Coverage Radius (km)" feature work?',
        a: 'When creating or editing a warehouse, you can define a Delivery Radius in kilometers (e.g., 10 km). The map will immediately display a live blue circular zone preview indicating your active coverage area. Orders or customer destinations within this circle are eligible for local delivery.',
      },
      {
        q: 'Can a single store have multiple pickup warehouses with distinct delivery zones?',
        a: 'Yes! Stores can configure multiple regional fulfillment centers, each with its own customized delivery radius (e.g. 5 km for dense city centers and 25 km for suburban hubs). The system automatically evaluates coverage based on the customer destination coordinates.',
      },
      {
        q: 'How can storefront or checkout verify if a customer address is deliverable?',
        a: 'Use the Delivery Coverage Check API: POST /api/public/warehouse/check-delivery-coverage with customer latitude & longitude. The API calculates real-time distance using the Haversine formula and returns deliverable: true/false with the nearest warehouse details.',
      },
    ],
  },
  {
    key: 'live-tracking',
    icon: <CarOutlined />,
    iconBg: '#ecfeff',
    iconColor: '#06b6d4',
    title: 'Live Tracking & Map',
    desc: 'Real-time GPS telemetry, route calculation, and customer live maps.',
    items: [
      {
        q: 'How does Live Tracking work?',
        a: 'The Live Tracking screen connects to driver mobile GPS coordinates. Click on any active driver in the right sidebar to focus the map and see their active delivery route.',
      },
      {
        q: 'How does the customer track their package?',
        a: 'Each dispatched shipment generates a unique public live-map URL (e.g. /live-map?token=...) that can be sent via SMS/WhatsApp so customers can view delivery arrival countdowns in real time.',
      },
      {
        q: 'Why does the route polyline not appear on the map?',
        a: 'Turn-by-turn route calculation requires both valid driver GPS broadcast and customer address latitude/longitude. If the customer entered an incomplete address, only the driver position will show.',
      },
    ],
  },
  {
    key: 'payments',
    icon: <CreditCardOutlined />,
    iconBg: '#fdf2f8',
    iconColor: '#ec4899',
    title: 'Payments & COD Reconciliation',
    desc: 'Cash-on-delivery tracking, prepaid settlements, and analytics.',
    items: [
      {
        q: 'Where do I view daily COD vs Prepaid collection totals?',
        a: 'Your Dashboard includes a dedicated "Today: Driver-wise COD vs Direct Analytics" card displaying driver collection breakdown, total cash received, and outstanding amounts.',
      },
      {
        q: 'How do I toggle COD / Prepaid for a specific order?',
        a: 'Open the Assign Driver modal or view the Order Details card to verify or update the Payment Type badge before shipment creation.',
      },
    ],
  },
  {
    key: 'api-docs',
    icon: <FileTextOutlined />,
    iconBg: '#f0fdf4',
    iconColor: '#16a34a',
    title: 'API & Developer Webhooks',
    desc: 'HMAC signature verification, REST endpoints, and code examples.',
    items: [
      {
        q: 'Where can I find complete developer documentation?',
        a: 'Visit the public /api-documentation page in the navigation bar. It includes ready-to-run code snippets for cURL, JavaScript, Python, and PHP.',
      },
      {
        q: 'Why am I receiving an "Invalid Signature" error?',
        a: 'HMAC-SHA256 signatures require alphabetically sorted JSON payload keys, exact timestamp tolerance (within 5 minutes), and the correct API secret key.',
      },
    ],
  },
  {
    key: 'troubleshooting',
    icon: <WarningOutlined />,
    iconBg: '#fef2f2',
    iconColor: '#ef4444',
    title: 'Troubleshooting & FAQ',
    desc: 'Common resolution steps for map rendering, uploads, and browser cache.',
    items: [
      {
        q: 'My recent dashboard updates are not appearing.',
        a: 'Perform a hard browser reload (Ctrl + Shift + R or Cmd + Shift + R) to ensure you are viewing the latest frontend build without stale browser cache.',
      },
      {
        q: 'Map is blank or Google Maps API key warning appears.',
        a: 'Verify that the Google Maps JavaScript API key is configured with Maps JavaScript and Geocoding APIs enabled in Google Cloud Console.',
      },
    ],
  },
];

export default function UserGuide() {
  const [q, setQ] = useState('');
  const [activeCategory, setActiveCategory] = useState('ALL');
  const navigate = useNavigate();

  const normalized = q.trim().toLowerCase();

  const filteredFaq = useMemo(() => {
    return FAQ.map((section) => {
      // Category filter check
      if (activeCategory !== 'ALL' && section.key !== activeCategory) {
        return null;
      }

      // Keyword search check
      const match = (s) => String(s || '').toLowerCase().includes(normalized);
      const items = (section.items || []).filter((it) => {
        if (!normalized) return true;
        return match(it.q) || match(it.a) || match(section.title) || match(section.desc);
      });

      if (items.length === 0) return null;

      return {
        ...section,
        items,
      };
    }).filter(Boolean);
  }, [normalized, activeCategory]);

  const collapsePanels = useMemo(() => {
    return filteredFaq.map((section) => ({
      key: section.key,
      label: (
        <div className="guide-panel-title">
          <div
            className="guide-panel-icon"
            style={{ background: section.iconBg, color: section.iconColor }}
          >
            {section.icon}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
              {section.title}
            </div>
            <div style={{ fontSize: 12.5, color: '#64748b', fontWeight: 400 }}>
              {section.desc}
            </div>
          </div>
        </div>
      ),
      children: (
        <div className="guide-content-grid">
          {section.items.map((it, idx) => (
            <div key={`${section.key}-${idx}`} className="guide-qa-card">
              <div className="guide-q-title">
                <QuestionCircleOutlined className="guide-q-icon" />
                <span>{it.q}</span>
              </div>
              <Paragraph className="guide-a-text">{it.a}</Paragraph>
            </div>
          ))}
        </div>
      ),
    }));
  }, [filteredFaq]);

  return (
    <MainLayout>
      <div className="user-guide-wrapper">
        {/* Modern Hero Section */}
        <div className="user-guide-hero">
          <div className="user-guide-hero-orb user-guide-hero-orb-1" />
          <div className="user-guide-hero-orb user-guide-hero-orb-2" />

          <div className="user-guide-hero-content">
            <div className="user-guide-badge">
              <BookOutlined /> Knowledge Base & User Guide
            </div>
            <Title level={1} className="user-guide-hero-title">
              How Can We Help You Today?
            </Title>
            <p className="user-guide-hero-desc">
              Explore step-by-step guides, troubleshooting instructions, and best practices for managing your Shopify delivery ecosystem.
            </p>

            {/* Quick Search */}
            <div className="user-guide-search-wrapper">
              <Input
                size="large"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: '#2563eb', fontSize: 18, marginRight: 8 }} />}
                placeholder="Search orders, driver KYC, GPS live tracking, webhooks, or COD..."
                className="user-guide-search-input"
              />
            </div>
          </div>
        </div>

        {/* Categories Pill Navigation */}
        <div className="user-guide-categories">
          <div
            className={`category-chip ${activeCategory === 'ALL' ? 'active' : ''}`}
            onClick={() => setActiveCategory('ALL')}
          >
            <ThunderboltOutlined className="chip-icon" /> All Topics ({FAQ.length})
          </div>
          {FAQ.map((cat) => (
            <div
              key={cat.key}
              className={`category-chip ${activeCategory === cat.key ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.key)}
            >
              <span className="chip-icon" style={{ color: cat.iconColor }}>{cat.icon}</span>
              {cat.title}
            </div>
          ))}
        </div>

        {/* Collapsible FAQ Panels */}
        {collapsePanels.length > 0 ? (
          <Collapse
            accordion={false}
            defaultActiveKey={['getting-started', 'orders']}
            items={collapsePanels}
            className="guide-accordion"
            bordered={false}
          />
        ) : (
          <Card style={{ borderRadius: 16, textAlign: 'center', padding: '36px 0' }}>
            <Empty
              description={
                <div>
                  <Text strong style={{ fontSize: 16 }}>No matching topics found for "{q}"</Text>
                  <div style={{ color: '#64748b', marginTop: 4 }}>
                    Try searching with different keywords or reset your filter.
                  </div>
                </div>
              }
            >
              <Button type="primary" onClick={() => { setQ(''); setActiveCategory('ALL'); }}>
                Reset Filters
              </Button>
            </Empty>
          </Card>
        )}

        {/* Still Need Help Support Card */}
        <div className="guide-help-card">
          <div>
            <div className="guide-help-title">Still have unanswered questions?</div>
            <p className="guide-help-desc">
              Our 24/7 technical operations team is here to assist with dispatch anomalies and API setups.
            </p>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<CustomerServiceOutlined />}
            onClick={() => navigate(routes.SUPPORT)}
            style={{
              background: '#2563eb',
              borderRadius: 10,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
            }}
          >
            Contact Support Team
          </Button>
        </div>
      </div>
    </MainLayout>
  );
}
